import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { fetchFeedbackSuggestion, submitFeedback } from './api';
import { useFeedbackInvitation } from './FeedbackInvitationContext';
import { useSpendingCap } from './SpendingCapContext';
import './Feedback.css';

// The currency an amount is counted in when the server has not said otherwise. It is the server's
// base currency too, so a fallback answer stays comparable with every other row.
const BASE_CURRENCY = 'USD';

// The slider counts in tenths of the proposal rather than in money: a position means the same
// thing in every currency, so changing currency re-prices the answer without moving the handle.
// Whole steps keep the arithmetic off binary fractions, which 0.1 is not.
const MIN_STEP = 1;
const MAX_STEP = 20;
const PROPOSAL_STEP = 10;

// The feedback screen (#207): a demo user tells us what the product was worth to them.
// Amount, comment and email are each independently optional. It is reachable directly by
// URL, before the funnel that redirects here exists.
//
// The amount is a slider spanning a tenth to twice what the server proposes, which makes the
// proposal a visible midpoint rather than a number to argue with. The proposal arrives per
// currency, so this file holds no list the server could disagree with. A slider cannot be empty,
// hence the checkbox: declining is said out loud rather than inferred from an untouched handle.
// Where there is nothing to scale — no proposal, or a proposal of zero — the field is typed into.
//
// Saying something and reading it back are two screens: this one asks, and hands the user to
// their feedback once they have answered, where the new entry is waiting.
function Feedback() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  // Set when the user was sent here by running out of AI allowance rather than by choosing
  // the screen themselves (#208); it explains the arrival they did not ask for.
  const { capped, cappedMessage } = useSpendingCap();
  // An answered invitation is a spent one (#212), whether the user came here from the banner or
  // found the screen on their own.
  const { markGiven } = useFeedbackInvitation();

  const [amount, setAmount] = useState('');
  // Where the slider sits, in tenths of the proposal. It opens on the proposal itself.
  const [steps, setSteps] = useState(PROPOSAL_STEP);
  // The base currency, so an amount is never submitted undenominated. The server's proposal
  // replaces it with whatever that lists first; if the proposal never arrives, this is what the
  // answer is counted in.
  const [currency, setCurrency] = useState(BASE_CURRENCY);
  const [comment, setComment] = useState('');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // What the server proposed: the spend it was derived from, and the ask per currency. Null until
  // it arrives, and stays null if it never does — a form nobody can answer would be worse than a
  // blank field.
  const [suggestion, setSuggestion] = useState(null);
  // True until the request settles, either way. A slider with no proposal behind it has no scale
  // to offer, so it is shown but not answerable rather than answerable against nothing.
  const [pending, setPending] = useState(true);
  // Set when the user says they would rather not name an amount. A slider always sits somewhere,
  // so without this an untouched handle would be submitted as an answer nobody gave.
  const [declined, setDeclined] = useState(false);

  const suggestions = suggestion?.suggestions ?? [];

  // What the slider is a proportion of. Zero is a real answer, not a missing one: nothing floors
  // the proposal any more, so a user who has spent nothing is anchored at nothing.
  const anchor = suggestions.find((entry) => entry.currency === currency)?.amount ?? 0;
  const scalable = anchor > 0;
  const scaled = (anchor * steps) / PROPOSAL_STEP;
  const money = (value) =>
    new Intl.NumberFormat(i18n.language, { style: 'currency', currency }).format(value);
  // Settled at two decimals before it leaves: what is sent is money, not the product of two
  // doubles, and never the formatted string the user reads.
  const answer = declined ? '' : scalable ? scaled.toFixed(2) : amount;
  // Neither an amount nor a currency is being given, so neither is adjustable.
  const locked = pending || declined;

  useEffect(() => {
    let abandoned = false;

    fetchFeedbackSuggestion()
      .then((proposal) => {
        if (abandoned) return;
        setSuggestion(proposal);
        // The server sends its base currency first, which is what the picker opens on.
        const first = proposal.suggestions?.[0];
        if (!first) return;
        setCurrency(first.currency);
      })
      .catch(() => {
        // No proposal: the user gets an empty field and can still answer.
      })
      .finally(() => {
        if (!abandoned) setPending(false);
      });

    return () => {
      abandoned = true;
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await submitFeedback({ amount: answer, currency, comment, email });
      toast.success(t('feedback.thankYou'));
      markGiven();
      // Handed to the record rather than left on an emptied form: what they just said is read
      // back from the server, which is the only place that knows its date and stored amount.
      navigate('/feedbacks');
    } catch (err) {
      toast.error(t('feedback.failed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="feedback">
      <Link className="feedback-back" to="/feedbacks">
        ‹ {t('sidebar.myFeedback')}
      </Link>
      <h1>{t('feedback.title')}</h1>
      {capped && (
        <div className="feedback-capped" role="status">
          <p>{t('feedback.cappedTitle')}</p>
          {cappedMessage && <p className="feedback-capped-reason">{cappedMessage}</p>}
        </div>
      )}
      <p className="feedback-intro">{t('feedback.intro')}</p>

      <form className="feedback-form" onSubmit={handleSubmit}>
        <label htmlFor="feedback-amount">{t('feedback.amount')}</label>
        <div className="feedback-amount-row">
          {pending || scalable ? (
            <>
              <input
                id="feedback-amount"
                type="range"
                min={MIN_STEP}
                max={MAX_STEP}
                step={1}
                value={steps}
                disabled={locked}
                // Without this a screen reader reads out the tenths, which are bookkeeping.
                aria-valuetext={scalable ? money(scaled) : undefined}
                onChange={(e) => setSteps(Number(e.target.value))}
              />
              {scalable && !declined && (
                <output className="feedback-amount-value" htmlFor="feedback-amount">
                  {money(scaled)}
                </output>
              )}
            </>
          ) : (
            <input
              id="feedback-amount"
              type="number"
              min="0"
              step="0.01"
              inputMode="decimal"
              value={amount}
              disabled={locked}
              onChange={(e) => setAmount(e.target.value)}
              placeholder={t('feedback.amountPlaceholder')}
            />
          )}
          <select
            aria-label={t('feedback.currency')}
            value={currency}
            disabled={locked}
            onChange={(e) => setCurrency(e.target.value)}
          >
            {/* Without a proposal there is nothing to convert, so only the base currency is on
                offer — an empty picker would be worse than a single choice. */}
            {(suggestions.length ? suggestions.map((entry) => entry.currency) : [BASE_CURRENCY]).map(
              (code) => (
                <option key={code} value={code}>
                  {code}
                </option>
              ),
            )}
          </select>
        </div>

        <label className="feedback-decline">
          <input
            type="checkbox"
            checked={declined}
            onChange={(e) => setDeclined(e.target.checked)}
          />
          {t('feedback.declineAmount')}
        </label>

        <label htmlFor="feedback-comment">{t('feedback.comment')}</label>
        <textarea
          id="feedback-comment"
          rows={4}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder={t('feedback.commentPlaceholder')}
        />

        <label htmlFor="feedback-email">{t('feedback.email')}</label>
        <input
          id="feedback-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={t('feedback.emailPlaceholder')}
        />
        <p className="feedback-email-help">{t('feedback.emailHelp')}</p>

        <button type="submit" className="feedback-submit" disabled={submitting}>
          {submitting ? t('feedback.submitting') : t('feedback.submit')}
        </button>
      </form>

    </div>
  );
}

export default Feedback;
